import Pusher from 'pusher-js';
import {
  configurePusher as configurePusherRedux,
  subscribe as pusherReduxSubscribe,
  unsubscribe as pusherReduxUnsubscribe,
} from 'pusher-redux';

import { LT_PUSHER } from 'bl/loggers.bl';
import FLAG from 'config/flag.singleton';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { fromAnyToPrettyJson } from 'utils/converters/json.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import isNotMissing from 'utils/predicates/any/isNotMissing.util';
import { isNotValidId } from 'utils/predicates/fields.util';

const L = logger(['PUSHER', LT_PUSHER]);

const listenRedux = (options) => {
  const salonId = options?.salonId;

  if (isNotValidId(salonId)) {
    L.warn('listenRedux()', 'Invalid salonId:', salonId, 'in options:', options);
    return alwaysNoop;
  }

  // NOTE: the following validations are just informational, will allow errors to be reported by BE

  const channelSuffix = fromAnyToTrimmed(options?.channelSuffix);
  if (!channelSuffix) L.warn('listenRedux()', 'Invalid channelSuffix:', channelSuffix, 'in options:', options);

  const channelName = `${String(salonId)}${channelSuffix}`;

  const eventName = fromAnyToTrimmed(options?.eventName);
  if (!eventName) L.warn('listenRedux()', 'Invalid eventName:', eventName, 'in options:', options);

  const actionType = fromAnyToTrimmed(options?.actionType) || fromAnyToTrimmed((options?.AT || options?.at)?.push);
  if (!actionType) L.warn('listenRedux()', 'Invalid actionType:', actionType, 'in options:', options);

  const info = { channelName, eventName, actionType };
  L.debug('listenRedux()', 'Subscribing', info, '...');
  PUSHER.redux.subscribe({ channelName, eventName, actionType });

  return () => {
    L.debug('listenRedux()', 'Unsubscribing', info, '...');
    PUSHER.redux.unsubscribe(channelName, eventName, actionType);
  };
};

const subRedux = (options) => {
  const { actionType, additionalParams, channelName, eventName } = options ?? {};
  return pusherReduxSubscribe(channelName, eventName, actionType, additionalParams);
};

const unsubRedux = (options) => {
  const { channelName, eventName, actionType } = options ?? {};
  return pusherReduxUnsubscribe(channelName, eventName, actionType);
};

const configRedux = (options) => {
  const { store, apiKey, authEndpoint, cluster, authTransport, encrypted } = options ?? {};

  if (!apiKey) {
    L.warn('configRedux()', 'Invalid apiKey:', apiKey);
    return false;
  }

  configurePusherRedux(store, apiKey, { cluster, authTransport, encrypted, authEndpoint });
  return true;
};

const determineChannelName = (options) => {
  const channelName = fromAnyToTrimmed(options?.channelName);
  if (channelName) return channelName;

  const salonId = fromAnyToTrimmed(options?.salonId);
  if (isNotMissing(salonId) && isNotValidId(salonId)) {
    L.warn('determineChannelName()', 'Invalid options.salonId:', options?.salonId);
    return '';
  }

  const hasSuffix = isNotMissing(options?.suffix);
  const suffix = hasSuffix ? fromAnyToTrimmed(options?.suffix) : '';
  if (hasSuffix && !suffix) L.warn('determineChannelName()', 'Invalid options.suffix:', options?.suffix);

  return salonId + (suffix || '_channel');
};

const singleFor =
  ({ sub, unsub }) =>
  (options) => {
    const FTAG = 'single()';
    const log = options?.log;

    const chName = determineChannelName(options);
    const evName = fromAnyToTrimmed(options?.eventName);
    const fnName = `single > ${chName} > ${evName}()`;
    const fn = options?.fn;

    const channel = sub(chName);
    const handler = (data, meta) => {
      try {
        const result = call(fnName, fn, { data, meta });
        if (log) L.debug(fnName, '===', JSON.stringify({ data, meta }), '->', result);
        return result;
      } catch (e) {
        L.debug(fnName, e, '!!!', fnName, data, meta);
      }
    };

    if (log) L.debug(FTAG, '+++', fnName, { channel, handler });
    channel.bind(evName, handler);

    // noinspection UnnecessaryLocalVariableJS
    const off = () => {
      if (log) L.debug(FTAG, '---', fnName);
      channel.unbind(evName, handler);
      unsub(chName);
    };

    return off;
  };

const onFor =
  ({ pusher }) =>
  (options) =>
    pusher.connection.bind(options?.type, options?.fn, options?.ctx);

const subFor =
  ({ pusher }) =>
  (channelName) =>
    pusher.subscribe(channelName);

const unsubFor =
  ({ pusher }) =>
  (channelName) =>
    pusher.unsubscribe(channelName);

const init = (conf) => {
  try {
    const key = fromAnyToTrimmed(conf?.pusherId);

    if (!key) return void L.warn('init()', 'Invalid key:', key);

    Pusher.logToConsole = fromAnyToBool(FLAG.pusherLogToConsole);
    // Pusher.log = nullBind(L.debug, 'init() > Pusher.log()');

    const pusher = new Pusher(key, {
      cluster: 'eu',
      channelAuthorization: { transport: 'jsonp', endpoint: `${conf?.apiRoot}websocket/` },
    });

    L.debug('init()', 'instance:', pusher);

    const on = onFor({ pusher });
    const sub = subFor({ pusher });
    const unsub = unsubFor({ pusher });

    on({ type: 'error', fn: (error) => void L.warn('on error:', error, fromAnyToPrettyJson(error)) });

    if (FLAG.pusherLogStateChange) {
      on({ type: 'state_change', fn: (ev) => void L.debug('on state_change:', ev?.previous, '->', ev?.current) });
    }

    PUSHER.instance = pusher;
    PUSHER.on = on;
    PUSHER.sub = sub;
    PUSHER.unsub = unsub;
    PUSHER.single = singleFor({ sub, unsub });
  } catch (e) {
    L.warn('init()', e);
  }
};

const notInitializedFor = (name) => (_) => void L.warn('()', `PUSHER is not initialized, cannot use "${name}"`);

const PUSHER = {
  init,

  on: notInitializedFor('on'),
  sub: notInitializedFor('sub'),
  unsub: notInitializedFor('unsub'),
  single: notInitializedFor('single'),

  redux: {
    configure: configRedux,
    subscribe: subRedux,
    unsubscribe: unsubRedux,
    listen: listenRedux,
  },
};

export default PUSHER;
